import { useEffect } from "react";

import * as Sentry from "@sentry/react";

import { useGetMarketStatus } from "~/v2/api/hooks";
import type { MarketPhase } from "~/v2/api/rest/data";
import {
  EmptyIcon,
  MoonIcon,
  MoonStarsIcon,
  SunHorizon,
  SunIcon,
} from "~/v2/components/common/icons";
import { MarketStatusBadgePresentation } from "./MarketStatusBadge.pres";

const textMap: Record<
  MarketPhase,
  {
    firstText: string;
    secondText: string;
    icon: JSX.Element;
    tooltip: string;
    emphasizeFirstText?: boolean;
    emphasizeSecondText?: boolean;
  }
> = {
  pre: {
    firstText: "Pre-Market",
    secondText: "Session",
    icon: <SunHorizon />,
    tooltip: "Early trading session (4:00 AM – 9:30 AM ET).",
    emphasizeFirstText: true,
  },
  core: {
    firstText: "Market",
    secondText: "Open",
    icon: <SunIcon />,
    tooltip: "Standard market session (9:30 AM – 4:00 PM ET).",
    emphasizeSecondText: true,
  },
  post: {
    firstText: "Extended",
    secondText: "Session",
    icon: <MoonIcon />,
    tooltip: "Extended trading session (4:00 PM – 8:00 PM ET).",
    emphasizeSecondText: true,
  },
  overnight: {
    firstText: "Overnight",
    secondText: "Session",
    icon: <MoonStarsIcon />,
    tooltip: "Overnight session (8:00 PM – 4:00 AM ET).",
    emphasizeSecondText: true,
  },
  closed: {
    firstText: "Market",
    secondText: "Closed",
    icon: <EmptyIcon />,
    tooltip: "Markets are closed for the weekend or a market holiday.",
    emphasizeSecondText: true,
  },
};

const isKnownMarketPhase = (phase: unknown): phase is MarketPhase =>
  typeof phase === "string" && Object.hasOwn(textMap, phase);

export const MarketStatusBadge = () => {
  const { marketStatus } = useGetMarketStatus();

  const currentPhase = marketStatus?.phase;
  const text = isKnownMarketPhase(currentPhase)
    ? textMap[currentPhase]
    : undefined;

  useEffect(() => {
    if (!marketStatus || text) {
      return;
    }

    Sentry.captureException(new Error("Unknown market status phase"), {
      level: "warning",
      tags: {
        component: "MarketStatusBadge",
        marketPhase: String(currentPhase),
      },
      extra: { marketStatus },
    });
  }, [currentPhase, marketStatus, text]);

  if (!marketStatus || !text) {
    return null;
  }

  const {
    firstText,
    emphasizeFirstText,
    secondText,
    emphasizeSecondText,
    icon,
    tooltip,
  } = text;

  return (
    <MarketStatusBadgePresentation
      isLoading={false}
      icon={icon}
      firstText={firstText}
      secondText={secondText}
      tooltip={tooltip}
      emphasizeFirstText={emphasizeFirstText}
      emphasizeSecondText={emphasizeSecondText}
    />
  );
};
